import classNames from 'classnames';
import debounce from 'debounce-promise';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { ITag, Tags as TagsCollection, TTagContext } from '@feathr/blackbox';
import { UniversalCreatableSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';

import * as styles from './TagsField.css';

interface IProps {
  className?: string;
  /** Defaults to the store's. */
  collection?: TagsCollection;
  context: TTagContext;
  disabled?: boolean;
  helpText?: string;
  hideInternalTags?: boolean;
  label?: React.ReactNode;
  onChange: (newValue: string[]) => void;
  value?: string[];
  wrapperClassName?: string;
}

function getOptionLabel(option: ITag): string {
  return option.name ? option.name : '';
}

function getOptionValue(option: ITag): string {
  return option.id ? option.id : '';
}

function getNewOptionData(inputValue: string, optionLabel: React.ReactNode): ITag {
  return { id: inputValue, name: optionLabel } as ITag;
}

function TagsField({
  className,
  collection,
  context,
  disabled = false,
  helpText,
  hideInternalTags = false,
  label,
  onChange,
  value = [],
  wrapperClassName,
}: Readonly<IProps>): JSX.Element {
  const { Tags: storeTags } = useStore();
  const Tags = collection ?? storeTags;
  const defaultTags = Tags.list({
    filters: { context },
    pagination: { page_size: 10 },
  }).models.map((t) => t.toJS());

  const loadOptions = debounce(async (inputValue: string) => {
    const tags = Tags.list({
      filters: { context, name__icontains: inputValue },
      pagination: { page_size: 1000 },
    });

    await when(() => !tags.isPending);
    const options = tags.models.map((tag) => tag.toJS());
    return hideInternalTags ? options.filter((tag) => !tag.internal) : options;
  }, DEFAULT_DEBOUNCE_WAIT);

  function isValidNewOption(inputValue: string, _: ITag | ITag[] | null, options: ITag[]): boolean {
    const tagModel = Tags.create({ context, name: inputValue });
    return (
      !!inputValue &&
      tagModel.isValid(['name'], false) &&
      !options.find((tag) => tag.name === inputValue)
    );
  }

  async function createOption(inputValue: string): Promise<ITag> {
    const tag = await Tags.add(Tags.create({ context, name: inputValue }));
    onChange([...value, tag.id]);
    return tag.toJS();
  }

  function handleChange(values: ITag[]): void {
    onChange(values.map((tag) => tag.id!));
  }

  const currentTags = value.map((tagId) => Tags.get(tagId).toJS());

  return (
    <UniversalCreatableSelect<ITag>
      async={true}
      className={classNames(styles.root, className)}
      createOption={createOption}
      defaultOptions={hideInternalTags ? defaultTags.filter((tag) => !tag.internal) : defaultTags}
      disabled={disabled}
      getNewOptionData={getNewOptionData}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      helpText={helpText}
      isMulti={true}
      isValidNewOption={isValidNewOption}
      key={'tags-field'}
      label={label}
      loadOptions={loadOptions}
      name={'tags-field'}
      onChange={handleChange}
      placeholder={'Tags'}
      value={hideInternalTags ? currentTags.filter((tag) => !tag.internal) : currentTags}
      wrapperClassName={wrapperClassName}
    />
  );
}

export default observer(TagsField);
